// External component imports
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';

// Local style imports
import { styles } from './ComplianceForm.style';

// Constants and types
import { COMPLIANCE, ID_VERIFICATION_ERROR_ALERT, ID_VERIFICATION_SUCCESS_ALERT } from './shared/constants';

// Local component imports
import { ComplianceAccordion } from './components/ComplianceAccordian';

//selectors and utilities store
import { useSelector } from 'react-redux';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { useEffect, useCallback } from 'react';
import { ResendIdRequest } from './components/ReSendIdRequest/ResendIdRequest';
import { SuccessErrorAlert } from './components/SuccessErrorAlert/SuccessErrorAlert';

interface CompliancePayload {
    dealXgId: string;
    dealXgVersion: string;
    dealerId: number;
    OFAC?: string | null;
    redFlags?: string | null;
    syntheticIDVerification?: string | null;
    idVerification?: string | null;
    actionType: string;
}

export const ComplianceForm = () => {
    const shouldShowComplianceElements = useSelector(complianceSelectors.shouldShowComplianceElements);
    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const error = useSelector(complianceSelectors.getResendIdVerificationError);
    const resendIdVerificationResponse = useSelector(complianceSelectors.getResendIdVerificationData);
    const logNRAction = useCallback(
        (name: string, actionType: string, complianceData?: any) => {
            const basePayload: CompliancePayload = {
                dealXgId,
                dealXgVersion,
                dealerId,
                actionType,
                OFAC: complianceData?.OFAC?.status || null,
                redFlags: complianceData?.redFlags?.status || null,
                syntheticIDVerification: complianceData?.syntheticIDVerification?.status || null,
                idVerification: complianceData?.idVerification?.status || null
            };

            logNewRelicPageAction(name, basePayload);
        },
        [dealXgId, dealXgVersion, dealerId, participantMatch]
    );

    useEffect(() => {
        if (shouldShowComplianceElements && participantMatch) {
            if (participantMatch.compliance) {
                logNRAction('MV:ComplianceForm - Data Found', 'COMPLIANCE_DATA_FOUND', participantMatch.compliance);
            } else {
                logNRAction('MV:ComplianceForm - No Data Found', 'COMPLIANCE_DATA_NOT_FOUND');
            }
        }
    }, [shouldShowComplianceElements, participantMatch]);

    const hasIdVerificationError = error !== null;
    const hasResendIdVerificationResponse = resendIdVerificationResponse !== null;

    const showSuccessMessage = hasResendIdVerificationResponse && !hasIdVerificationError;
    const showErrorMessage = hasIdVerificationError;

    return (
        <Grid sx={styles.container}>
            <Typography variant="strong-lg">{COMPLIANCE.HEADING}</Typography>
            <Typography sx={styles.heading} variant="body-sm">
                {COMPLIANCE.SUBHEADING}
            </Typography>

            <ComplianceAccordion />

            {/* ID Verification Section */}
            <>
                {showSuccessMessage && (
                    <SuccessErrorAlert
                        title={ID_VERIFICATION_SUCCESS_ALERT.TITLE}
                        description={''}
                        testId="id-verification-success-alert"
                        separatorTestId="id_verification_card_separator"
                        type="success"
                        hideSeparator={true}
                    />
                )}

                {showErrorMessage && (
                    <SuccessErrorAlert
                        title={ID_VERIFICATION_ERROR_ALERT.TITLE}
                        description={ID_VERIFICATION_ERROR_ALERT.DESCRIPTION}
                        testId="id-verification-error-alert"
                        separatorTestId="id_verification_card_separator"
                        type="error"
                        hideSeparator={true}
                    />
                )}

                <ResendIdRequest />
            </>
        </Grid>
    );
};
